// FZ 1.3 — Tela de Deliberação (/financeiro/deliberacao).
// Consome a fila financeiro.divergencias (FZ 1.2). Para cada divergência mostra
// o painel das fontes (Notion×CA×Sistema, do snapshot) e as ações contextuais;
// "aplicar aos N iguais" é batch com dry-run obrigatório. O Lucas delibera aqui.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Button, Table, Tag, Space, Modal, Select, Input, InputNumber,
  Radio, Tabs, Popconfirm, Tooltip, Typography, Alert, Descriptions,
} from 'antd';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
// Avisos do resultado por contrato (05/10/2026): todos numa aba só — antes não
// apareciam em aba nenhuma. Tipos, nomes e "como resolver" em utils.
import {
  ABA_RESULTADO_CONTRATO, ROTULO_DO_AVISO, ACOES_DO_AVISO, SOBRE_IGNORAR,
  ehAvisoDoResultado, comoResolver, abertosDoResultado,
} from '../utils/avisosResultadoContrato';

const { Text } = Typography;

type Sugestao = { acao?: string; texto?: string };
type Candidato = { id: string; titulo: string; origem: string; data_vencimento?: string | null };
type Gemea = { id: string; titulo: string; status: string; vencimento?: string | null; recebido?: number };
type Snapshot = {
  motivo?: string; como_resolveu?: string; status_notion?: string;
  valor_bruto?: number; valor_liquido?: number; candidatos?: Candidato[];
  orfa_vencimento?: string | null;
  padrao?: string; motivo_fila?: string; valor?: number; aditivo?: number;
  soma_parcelas_notion?: number; sistema_numero?: string;
  // tipos Notion×Sistema (rateio/fantasma/duplicata)
  status?: string; vencimento?: string | null; recebido?: number; filhos_qtd?: number;
  vencimento_notion?: string | null; recebido_notion?: number; gemeas?: Gemea[];
  // valor_divergente_notion
  valor_sistema?: number; valor_notion?: number; diferenca?: number; titulo_notion?: string;
  // parcela_so_no_sistema
  contrato_numero?: string; nf_situacao?: string; emissao?: string | null;
  // recebimentos_ca_soltos (agregado por contrato)
  qtd?: number; soma?: number; maiores?: { valor: number; data: string; descricao: string }[];
  // negocio_ganho_sem_credito (REMVAR F1)
  proposta_id?: number; responsavel_texto?: string | null; origem?: string | null;
  // competencia_diverge_emissao (2026-08-05)
  competencia?: string; mes_emissao?: string; mes_competencia?: string;
  delta_meses?: number; status_parcela?: string;
};
type Divergencia = {
  id: string; tipo: string; titulo: string; valor: number | null;
  severidade: 'alta' | 'media' | 'baixa'; status: string;
  contrato_id: string | null; contrato_numero: string | null;
  parcela_id: string | null; snapshot: Snapshot; sugestao: Sugestao;
  resolucao?: any;
};

const fmt = (v: number | null | undefined) =>
  v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtVenc = (v?: string | null) => (v ? v.split('-').reverse().join('/') : 's/ venc');

const SEV_COR: Record<string, string> = { alta: 'red', media: 'orange', baixa: 'default' };
const TIPO_LABEL: Record<string, string> = {
  parcela_orfa_notion: 'Parcela órfã (Notion)',
  contrato_mapa_fila: 'Contrato/aditivo (mapa)',
  rateio_pendente_notion: 'Rateio por área pendente',
  parcela_fantasma_notion: 'Página apagada no Notion',
  duplicata_plano_notion: 'Duplicata plano × Notion',
  valor_divergente_notion: 'Valor difere do Notion',
  parcela_so_no_sistema: 'Só no sistema (falta no Notion)',
  recebimentos_ca_soltos: 'Recebimentos CA sem parcela',
  // REMVAR F1 (2026-08-04): negócio ganho com contrato ativo e nenhum crédito
  // comercial registrado — resolve-se registrando na aba Créditos do negócio
  negocio_ganho_sem_credito: 'Ganho sem crédito comercial',
  // 2026-08-05: receita caindo na DRE num mês diferente do mês da NF —
  // política "competência = faturamento"; divergência é exceção deliberável
  competencia_diverge_emissao: 'Competência ≠ emissão (DRE)',
  ...ROTULO_DO_AVISO,
};

// Áreas do rateio (mesmas chaves do backend / colunas % do Notion)
const AREAS_RATEIO: { k: string; l: string }[] = [
  { k: 'topografia', l: 'Topografia' },
  { k: 'hidrometria', l: 'Hidrometria' },
  { k: 'projeto', l: 'Projeto' },
  { k: 'consultoria_ambiental', l: 'Consultoria Ambiental' },
  { k: 'fiscalizacao_projeto', l: 'Fiscalização de Projeto' },
  { k: 'seguranca_barragem', l: 'Segurança de Barragem' },
];

// getToken() é ASSÍNCRONO e a convenção do app é Authorization: token (sem
// "Bearer") — mesmo padrão de FinanceiroContasReceber. Sem o await o header
// virava "Bearer [object Promise]" → 401.
const api = async (action: string, extra: any = {}) => {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
    { action, ...extra }, { headers: { Authorization: token } });
  return r.data;
};

export default function FinanceiroDeliberacao() {
  return <AntApp><Inner /></AntApp>;
}

function Inner() {
  const { message, modal } = AntApp.useApp();
  const [itens, setItens] = useState<Divergencia[]>([]);
  const [resumo, setResumo] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Abre na aba que vem primeiro (os avisos do resultado por contrato). Abrir em
  // "Órfãs Notion" — a segunda, hoje vazia — escondia a lista atrás de um clique.
  const [tab, setTab] = useState<string>(ABA_RESULTADO_CONTRATO);
  const [selecao, setSelecao] = useState<Record<string, string[]>>({});   // por tab
  const [resolvendo, setResolvendo] = useState<Divergencia | null>(null);
  const [batchAcao, setBatchAcao] = useState<string>('');

  async function carregar() {
    setLoading(true);
    try {
      const r = await api('fin_listar_divergencias', { status: 'aberta' });
      setItens(r.itens || []);
      setResumo(r.resumo || []);
    } catch (e: any) { message.error('Erro ao carregar: ' + (e?.response?.data?.error || e.message)); }
    setLoading(false);
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const porTab = useMemo(
    () => itens.filter(d => (tab === ABA_RESULTADO_CONTRATO ? ehAvisoDoResultado(d.tipo) : d.tipo === tab)),
    [itens, tab]);
  const abertasPorTipo = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of resumo) if (r.status === 'aberta') m[r.tipo] = r.qtd;
    return m;
  }, [resumo]);

  // Limpa a seleção da aba (evita id fantasma no lote após a lista mudar).
  const limparSelecao = () => setSelecao(s => ({ ...s, [tab]: [] }));

  async function redetectar() {
    try {
      const r = await api('fin_detectar_divergencias', { dry_run: false });
      message.success(`Detector: ${r.inseridas} novas, ${r.atualizadas} atualizadas, ${r.obsoletadas} fechadas`);
      limparSelecao();
      carregar();
    } catch (e: any) { message.error(e?.response?.data?.error || e.message); }
  }

  // (2026-07-13) "Trazer do Notion + re-verificar" removido: o Notion deixou
  // de ser fonte do CR — rateio/emissão/NF são editados no sistema. As actions
  // backend seguem existindo como rota manual de emergência.

  async function resolverUma(d: Divergencia, acao: string, params: any = {}) {
    try {
      await api('fin_resolver_divergencia', { id: d.id, acao, params });
      message.success('Resolvida');
      setResolvendo(null);
      limparSelecao();
      carregar();
    } catch (e: any) { message.error(e?.response?.data?.error || e.message); }
  }

  // "Unificar automáticas": resolve de vez os casos INEQUÍVOCOS (1 parcela de
  // plano + no máx 1 do Conta Azul). Ambíguos (ex: 12 parcelas iguais) ficam
  // pra resolver na mão. Dry-run mostra o que faria antes de confirmar.
  async function unificarAutomaticas() {
    const ids = porTab.map(x => x.id);
    if (!ids.length) { message.info('Nada pra unificar'); return; }
    try {
      const prev = await api('fin_unificar_tres_lote', { ids, dry_run: true });
      modal.confirm({
        title: `Unificar ${prev.unificaveis.length} parcela(s) automaticamente?`,
        width: 580,
        content: (
          <div style={{ maxHeight: 340, overflow: 'auto' }}>
            <p>Cada uma funde o Notion na parcela do sistema{' '}
              e amarra o Conta Azul como recebido.</p>
            {prev.ambiguas.length > 0 && <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message={`${prev.ambiguas.length} ambígua(s) ficam pra resolver na mão (várias parcelas de mesmo valor)`} />}
            <ul style={{ paddingLeft: 18 }}>
              {prev.unificaveis.map((u: any) => <li key={u.id}>{u.titulo} — {fmt(u.valor)}{u.tem_ca ? ' (com CA)' : ' (só Notion+sistema)'}</li>)}
            </ul>
          </div>
        ),
        okText: 'Confirmar', cancelText: 'Cancelar',
        okButtonProps: { disabled: prev.unificaveis.length === 0 },
        onOk: async () => {
          const r = await api('fin_unificar_tres_lote', { ids, dry_run: false });
          message.success(`${r.unificadas} unificada(s); ${r.ambiguas} ambíguas restam; ${r.erros.length} erro(s)`);
          limparSelecao();
          carregar();
        },
      });
    } catch (e: any) { message.error(e?.response?.data?.error || e.message); }
  }

  // "Unificar por ordem (N/N)": resolve as ambíguas de parcelamento (12 parcelas
  // iguais, ex HY26041) pareando a "Parcela N/M" do Notion com a N-ésima parcela
  // do plano por ordem de vencimento. Dry-run mostra o pareamento (N → vencimento)
  // pra conferir antes de aplicar.
  async function unificarPorOrdem() {
    const ids = porTab.map(x => x.id);
    if (!ids.length) { message.info('Nada pra unificar'); return; }
    try {
      const prev = await api('fin_unificar_por_ordem', { ids, dry_run: true });
      const pares = prev.pares || [];
      modal.confirm({
        title: `Unificar ${pares.length} parcela(s) por vencimento?`,
        width: 660,
        content: (
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            <p>Cada parcela do Notion entra numa parcela do plano; o <b>vencimento real</b> (do Notion) é{' '}
              adotado no plano, e a linha do Conta Azul do <b>mesmo vencimento</b> vira o recebido.{' '}
              Confira as datas antes de confirmar.</p>
            {prev.nao_resolvidas?.length > 0 && <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message={`${prev.nao_resolvidas.length} ficam de fora (resolver na mão)`}
              description={<ul style={{ paddingLeft: 16, margin: 0 }}>
                {prev.nao_resolvidas.map((n: any, i: number) => <li key={i}>{n.titulo} — {n.motivo}</li>)}
              </ul>} />}
            <Table size="small" rowKey="dvg_id" pagination={false} dataSource={pares}
              columns={[
                { title: 'Notion', dataIndex: 'titulo', ellipsis: true },
                { title: 'Venc. real', dataIndex: 'venc', width: 110, render: fmtVenc },
                { title: 'Plano', width: 130, align: 'center' as const,
                  render: (_: any, p: any) => p.adota_venc
                    ? <Tooltip title="o plano tinha data-placeholder; adota a data real"><Tag color="blue">adota data</Tag></Tooltip>
                    : <Tag>já casava</Tag> },
                { title: 'CA (venc)', width: 110, align: 'center' as const,
                  render: (_: any, p: any) => p.tem_ca ? fmtVenc(p.ca_venc) : (p.ca_obs ? <Tooltip title={p.ca_obs}>—</Tooltip> : '—') },
              ]} />
          </div>
        ),
        okText: 'Confirmar', cancelText: 'Cancelar',
        okButtonProps: { disabled: pares.length === 0 },
        onOk: async () => {
          const r = await api('fin_unificar_por_ordem', { ids, dry_run: false });
          message.success(`${r.unificadas} unificada(s) por ordem; ${r.nao_resolvidas} de fora; ${r.erros.length} erro(s)`);
          limparSelecao();
          carregar();
        },
      });
    } catch (e: any) { message.error(e?.response?.data?.error || e.message); }
  }

  // "Aplicar aos N iguais": dry-run → confirma.
  async function aplicarLote() {
    const ids = selecao[tab] || [];
    if (!ids.length || !batchAcao) { message.warning('Selecione linhas e uma ação'); return; }
    if (['mesclar_orfa', 'adotar_orfa'].includes(batchAcao)) {
      // essas exigem parâmetro por-linha (irma/contrato) — não fazem sentido em lote cego
      message.warning('Mesclar/Adotar em lote precisa de alvo por linha — use "Resolver" em cada uma');
      return;
    }
    try {
      const prev = await api('fin_resolver_divergencias_lote', { ids, acao: batchAcao, dry_run: true });
      const rotuloDaAcao = (acoesLoteDisponiveis.find(a => a.v === batchAcao) || { l: batchAcao }).l;
      modal.confirm({
        title: `Aplicar "${rotuloDaAcao}" a ${prev.aplicaveis.length} divergência(s)?`,
        width: 560,
        content: (
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            {prev.ja_resolvidas > 0 && <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message={`${prev.ja_resolvidas} já resolvida(s) serão ignoradas`} />}
            <ul style={{ paddingLeft: 18 }}>
              {prev.aplicaveis.map((a: any) => <li key={a.id}>{a.titulo}{a.valor != null ? ` — ${fmt(a.valor)}` : ''}</li>)}
            </ul>
          </div>
        ),
        okText: 'Confirmar', cancelText: 'Cancelar',
        onOk: async () => {
          const r = await api('fin_resolver_divergencias_lote', { ids, acao: batchAcao, dry_run: false });
          message.success(`${r.aplicadas} aplicada(s), ${r.erros.length} erro(s)`);
          setSelecao(s => ({ ...s, [tab]: [] }));
          carregar();
        },
      });
    } catch (e: any) { message.error(e?.response?.data?.error || e.message); }
  }

  const acoesLoteDisponiveis = tab === 'competencia_diverge_emissao'
    ? [{ v: 'usar_competencia_emissao', l: 'Usar mês da emissão' }, { v: 'ignorar', l: 'Ignorar' }]
    : tab === ABA_RESULTADO_CONTRATO
    ? [...ACOES_DO_AVISO]
    : tab === 'contrato_mapa_fila'
    ? [{ v: 'marcar_resolvida', l: 'Marcar resolvida' }, { v: 'ignorar', l: 'Ignorar' }]
    : tab === 'parcela_fantasma_notion'
      ? [{ v: 'cancelar_parcela', l: 'Cancelar parcela' }, { v: 'ignorar', l: 'Ignorar' }]
      : tab === 'valor_divergente_notion'
        ? [{ v: 'adotar_valor_notion', l: 'Adotar valor do Notion' }, { v: 'ignorar', l: 'Ignorar' }]
        : tab === 'parcela_so_no_sistema' || tab === 'recebimentos_ca_soltos'
          ? [{ v: 'marcar_resolvida', l: 'Marcar resolvida' }, { v: 'ignorar', l: 'Ignorar' }]
          : tab === 'rateio_pendente_notion' || tab === 'duplicata_plano_notion'
            ? [{ v: 'ignorar', l: 'Ignorar' }]
            : [{ v: 'excluir_orfa', l: 'Excluir órfã' }, { v: 'ignorar', l: 'Ignorar' }];

  // Na aba dos avisos do resultado por contrato o título É a informação (quem,
  // quanto, de que mês): quebra em linhas em vez de cortar. E as colunas Valor
  // e Sugestão saem — esses avisos não têm nem uma nem outra, seriam só traços.
  const naAbaDoResultado = tab === ABA_RESULTADO_CONTRATO;
  const colunas: any[] = [
    { title: 'Severidade', dataIndex: 'severidade', width: 110,
      render: (s: string) => <Tag color={SEV_COR[s]}>{s}</Tag> },
    { title: 'Título', dataIndex: 'titulo', ellipsis: !naAbaDoResultado,
      render: (t: string, d: Divergencia) => (
        <span>{t}{d.contrato_numero ? <Text type="secondary"> · {d.contrato_numero}</Text> : ''}
          {ehAvisoDoResultado(d.tipo) && <div><Text type="secondary" style={{ fontSize: 11 }}>{TIPO_LABEL[d.tipo] || d.tipo}</Text></div>}
        </span>) },
    ...(naAbaDoResultado ? [] : [
      { title: 'Valor', dataIndex: 'valor', width: 130, align: 'right' as const, render: fmt },
      { title: 'Sugestão', dataIndex: 'sugestao', width: 170,
        render: (s: Sugestao) => <Tooltip title={s?.texto}><Tag>{s?.acao || '—'}</Tag></Tooltip> },
    ]),
    { title: 'Ação', width: 120, render: (_: any, d: Divergencia) =>
        <Button size="small" type="primary" ghost onClick={() => setResolvendo(d)}>Resolver</Button> },
  ];

  // "Resultado por contrato" vem PRIMEIRO: são 11 abas e a última cai atrás do
  // "…" — e foi por não aparecerem que 19 avisos ficaram meses sem ninguém ver.
  const tabsItems = [
    { key: ABA_RESULTADO_CONTRATO, label: `Resultado por contrato (${abertosDoResultado(abertasPorTipo)})` },
    { key: 'parcela_orfa_notion', label: `Órfãs Notion (${abertasPorTipo['parcela_orfa_notion'] || 0})` },
    { key: 'contrato_mapa_fila', label: `Mapa contratos (${abertasPorTipo['contrato_mapa_fila'] || 0})` },
    { key: 'rateio_pendente_notion', label: `Rateio pendente (${abertasPorTipo['rateio_pendente_notion'] || 0})` },
    { key: 'parcela_fantasma_notion', label: `Fantasmas Notion (${abertasPorTipo['parcela_fantasma_notion'] || 0})` },
    { key: 'duplicata_plano_notion', label: `Duplicatas (${abertasPorTipo['duplicata_plano_notion'] || 0})` },
    { key: 'valor_divergente_notion', label: `Valor difere (${abertasPorTipo['valor_divergente_notion'] || 0})` },
    { key: 'parcela_so_no_sistema', label: `Falta no Notion (${abertasPorTipo['parcela_so_no_sistema'] || 0})` },
    { key: 'recebimentos_ca_soltos', label: `CA sem parcela (${abertasPorTipo['recebimentos_ca_soltos'] || 0})` },
    { key: 'negocio_ganho_sem_credito', label: `Sem crédito comercial (${abertasPorTipo['negocio_ganho_sem_credito'] || 0})` },
    { key: 'competencia_diverge_emissao', label: `Competência × emissão (${abertasPorTipo['competencia_diverge_emissao'] || 0})` },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0 }}>Deliberação de divergências</h2>
          <Text type="secondary">Resolva as pendências que a migração do Notion deixou. Decisões mudam dado financeiro.</Text>
        </div>
        <Space>
          {tab === 'parcela_orfa_notion' && (
            <Button type="primary" onClick={unificarAutomaticas}>⚡ Unificar automáticas</Button>
          )}
          {tab === 'parcela_orfa_notion' && (
            <Button onClick={unificarPorOrdem}>🔢 Unificar por ordem (N/N)</Button>
          )}
          <Popconfirm title="Redetectar divergências? (idempotente — não duplica)" onConfirm={redetectar}>
            <Button>Redetectar</Button>
          </Popconfirm>
        </Space>
      </Space>

      <Tabs activeKey={tab} onChange={t => { setTab(t); setBatchAcao(''); }} items={tabsItems} />

      {(selecao[tab]?.length || 0) > 0 && (
        <Space style={{ marginBottom: 8 }}>
          <Text strong>{selecao[tab].length} selecionada(s):</Text>
          <Select placeholder="Ação em lote" style={{ width: 200 }} value={batchAcao || undefined}
            onChange={setBatchAcao} options={acoesLoteDisponiveis.map(a => ({ value: a.v, label: a.l }))} />
          <Button type="primary" onClick={aplicarLote}>Aplicar aos {selecao[tab].length} (dry-run)</Button>
          <Button onClick={() => setSelecao(s => ({ ...s, [tab]: [] }))}>Limpar</Button>
        </Space>
      )}

      <Table
        rowKey="id" size="small" loading={loading} columns={colunas} dataSource={porTab}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        rowSelection={{
          selectedRowKeys: selecao[tab] || [],
          onChange: (keys) => setSelecao(s => ({ ...s, [tab]: keys as string[] })),
        }}
      />

      {resolvendo && (
        <ModalResolver d={resolvendo} onClose={() => setResolvendo(null)}
          onResolver={resolverUma} />
      )}
    </div>
  );
}

// ── Modal de resolução de UMA divergência (painel das fontes + ações) ────────
function ModalResolver({ d, onClose, onResolver }: {
  d: Divergencia; onClose: () => void;
  onResolver: (d: Divergencia, acao: string, params?: any) => void;
}) {
  const s = d.snapshot || {};
  const ehOrfa = d.tipo === 'parcela_orfa_notion';
  const ehRateio = d.tipo === 'rateio_pendente_notion';
  const ehFantasma = d.tipo === 'parcela_fantasma_notion';
  const ehDup = d.tipo === 'duplicata_plano_notion';
  const ehValor = d.tipo === 'valor_divergente_notion';
  const ehSoSis = d.tipo === 'parcela_so_no_sistema';
  const ehCaSolto = d.tipo === 'recebimentos_ca_soltos';
  const ehCredito = d.tipo === 'negocio_ganho_sem_credito';
  const ehCompEmi = d.tipo === 'competencia_diverge_emissao';
  const candidatos = s.candidatos || [];
  const candsSistema = candidatos.filter(c => c.origem !== 'conta_azul_api');
  const candsCA = candidatos.filter(c => c.origem === 'conta_azul_api');
  const [sistemaSel, setSistemaSel] = useState<string | undefined>(
    candsSistema.length === 1 ? candsSistema[0].id : undefined);
  const [caSel, setCaSel] = useState<string>(candsCA.length === 1 ? candsCA[0].id : '');
  const [nota, setNota] = useState('');
  // rateio_pendente_notion: percentuais por área (soma deve dar 100)
  const [pcts, setPcts] = useState<Record<string, number | null>>({});
  const somaPcts = AREAS_RATEIO.reduce((t, a) => t + (pcts[a.k] || 0), 0);
  // duplicata_plano_notion: a gêmea do plano que FICA
  const gemeas = s.gemeas || [];
  const [gemeaSel, setGemeaSel] = useState<string | undefined>(
    gemeas.length === 1 ? gemeas[0].id : undefined);

  return (
    <Modal open width={640} onCancel={onClose} footer={null}
      title={<span style={{ display: 'block', paddingRight: 28 }}><Tag color={SEV_COR[d.severidade]}>{d.severidade}</Tag> {d.titulo}</span>}>
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Tipo">{TIPO_LABEL[d.tipo] || d.tipo}</Descriptions.Item>
        {d.contrato_numero && <Descriptions.Item label="Contrato">{d.contrato_numero}</Descriptions.Item>}
        {d.valor != null && <Descriptions.Item label="Valor">{fmt(d.valor)}</Descriptions.Item>}
        {ehOrfa && <>
          <Descriptions.Item label="Motivo">{s.motivo}</Descriptions.Item>
          <Descriptions.Item label="Bruto / Líquido (Notion)">{fmt(s.valor_bruto)} / {fmt(s.valor_liquido)}</Descriptions.Item>
          {s.status_notion && <Descriptions.Item label="Status no Notion">{s.status_notion}</Descriptions.Item>}
        </>}
        {(ehRateio || ehFantasma) && <>
          {s.status && <Descriptions.Item label="Status">{s.status}</Descriptions.Item>}
          {s.vencimento && <Descriptions.Item label="Vencimento">{fmtVenc(s.vencimento)}</Descriptions.Item>}
          {ehFantasma && <Descriptions.Item label="Recebido">{fmt(s.recebido || 0)}</Descriptions.Item>}
        </>}
        {ehDup && <>
          {s.status_notion && <Descriptions.Item label="Status (linha Notion)">{s.status_notion}</Descriptions.Item>}
          {s.vencimento_notion && <Descriptions.Item label="Vencimento (linha Notion)">{fmtVenc(s.vencimento_notion)}</Descriptions.Item>}
          <Descriptions.Item label="Recebido (linha Notion)">{fmt(s.recebido_notion || 0)}</Descriptions.Item>
        </>}
        {ehValor && <>
          <Descriptions.Item label="Valor no sistema">{fmt(s.valor_sistema)}</Descriptions.Item>
          <Descriptions.Item label="Valor no Notion">{fmt(s.valor_notion)}</Descriptions.Item>
          <Descriptions.Item label="Diferença">{fmt(s.diferenca)}</Descriptions.Item>
          {(s.recebido || 0) > 0 && <Descriptions.Item label="Recebido">{fmt(s.recebido)}</Descriptions.Item>}
          {s.vencimento && <Descriptions.Item label="Vencimento">{fmtVenc(s.vencimento)}</Descriptions.Item>}
          {s.titulo_notion && <Descriptions.Item label="Título no Notion">{s.titulo_notion}</Descriptions.Item>}
        </>}
        {ehSoSis && <>
          {s.status && <Descriptions.Item label="Status">{s.status}</Descriptions.Item>}
          {s.nf_situacao && <Descriptions.Item label="Situação da NF">{s.nf_situacao}</Descriptions.Item>}
          {s.vencimento && <Descriptions.Item label="Vencimento">{fmtVenc(s.vencimento)}</Descriptions.Item>}
          {s.emissao && <Descriptions.Item label="Emissão prevista/real">{fmtVenc(s.emissao)}</Descriptions.Item>}
        </>}
        {ehCaSolto && <>
          <Descriptions.Item label="Linhas soltas">{s.qtd}</Descriptions.Item>
          <Descriptions.Item label="Soma recebida">{fmt(s.soma)}</Descriptions.Item>
        </>}
        {ehCredito && <>
          {s.proposta_id && <Descriptions.Item label="Negócio">#{s.proposta_id}</Descriptions.Item>}
          {s.responsavel_texto && <Descriptions.Item label="Responsável (texto legado)">{s.responsavel_texto}</Descriptions.Item>}
          {s.origem && <Descriptions.Item label="Origem">{s.origem}</Descriptions.Item>}
        </>}
        {ehCompEmi && <>
          <Descriptions.Item label="Emissão da NF">{fmtVenc(s.emissao)}</Descriptions.Item>
          <Descriptions.Item label="Competência atual (mês na DRE)">{fmtVenc(s.competencia)}</Descriptions.Item>
          {s.status_parcela && <Descriptions.Item label="Status da parcela">{s.status_parcela}</Descriptions.Item>}
        </>}
        {!ehOrfa && !ehRateio && !ehFantasma && !ehDup && !ehValor && !ehSoSis && !ehCaSolto && !ehCredito && !ehCompEmi && <>
          {s.padrao && <Descriptions.Item label="Padrão">{s.padrao}</Descriptions.Item>}
          {s.motivo_fila && <Descriptions.Item label="Motivo">{s.motivo_fila}</Descriptions.Item>}
          {s.aditivo != null && <Descriptions.Item label="Aditivo">{fmt(s.aditivo)}</Descriptions.Item>}
          {s.soma_parcelas_notion != null && <Descriptions.Item label="Soma parcelas (Notion)">{fmt(s.soma_parcelas_notion)}</Descriptions.Item>}
        </>}
      </Descriptions>

      {ehRateio ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="Sem divisão por área"
            description="Defina a divisão por área aqui (o Notion não é mais fonte do CR). Sem divisão, a receita aparece como 'Não discriminado' na DRE." />
          <div>
            {AREAS_RATEIO.map(a => (
              <div key={a.k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ width: 190 }}>{a.l}</span>
                <InputNumber min={0} max={100} addonAfter="%" style={{ width: 130 }}
                  value={pcts[a.k] ?? undefined}
                  onChange={v => setPcts(p => ({ ...p, [a.k]: v == null ? null : Number(v) }))} />
              </div>
            ))}
            <Text type={Math.abs(somaPcts - 100) < 0.01 ? 'success' : 'secondary'}>
              Soma: {somaPcts.toFixed(1)}% {Math.abs(somaPcts - 100) >= 0.01 && somaPcts > 0 ? '(precisa dar 100%)' : ''}
            </Text>
          </div>
          <Space wrap>
            <Button type="primary" disabled={Math.abs(somaPcts - 100) >= 0.01}
              onClick={() => {
                const rateio: Record<string, number> = {};
                for (const a of AREAS_RATEIO) if ((pcts[a.k] || 0) > 0) rateio[a.k] = pcts[a.k] as number;
                onResolver(d, 'definir_rateio', { rateio, nota });
              }}>
              Definir rateio
            </Button>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar (ex.: contrato não iniciou)</Button>
          </Space>
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
        </Space>
      ) : ehFantasma ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="warning" showIcon message="A página desta parcela foi apagada no Notion"
            description="Se a parcela não vale mais (ex.: contrato substituído), cancele — ela vira histórico e sai da receita. Se vale, desvincule do Notion e mantenha viva." />
          {(s.filhos_qtd || 0) > 0 && (
            <Alert type="error" showIcon
              message={`Esta parcela tem ${s.filhos_qtd} linha(s) do Conta Azul conciliada(s) embaixo — cancelar/excluir é bloqueado; resolva na aba Financeiro do contrato.`} />
          )}
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space wrap>
            <Popconfirm title="Cancelar a parcela? (sai da receita, fica como histórico)"
              onConfirm={() => onResolver(d, 'cancelar_parcela', { nota })}>
              <Button type="primary" danger>Cancelar parcela</Button>
            </Popconfirm>
            <Popconfirm title="Manter a parcela viva e tirar o vínculo com o Notion?"
              onConfirm={() => onResolver(d, 'desvincular_manter', { nota })}>
              <Button>Desvincular e manter</Button>
            </Popconfirm>
            <Popconfirm title="Excluir de vez? (só sem recebido e sem anexos/OFX)"
              onConfirm={() => onResolver(d, 'excluir_parcela', {})}>
              <Button danger>Excluir</Button>
            </Popconfirm>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehDup ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="error" showIcon message="Possível parcela em dobro"
            description="A linha do Notion (acima) tem gêmea(s) do plano no mesmo contrato e valor. Mesclar: a linha do Notion some e a do plano herda o vínculo e o realizado. Se forem de fato duas parcelas distintas, mantenha as duas." />
          <div>
            <Text strong>Gêmea do plano — a parcela que fica:</Text>
            <Radio.Group value={gemeaSel} onChange={e => setGemeaSel(e.target.value)}
              style={{ display: 'block', marginTop: 4 }}>
              <Space direction="vertical">
                {gemeas.map(g => (
                  <Radio key={g.id} value={g.id}>
                    <Text strong>venc {fmtVenc(g.vencimento)}</Text>
                    <Text type="secondary"> · {g.status}{g.titulo ? ` · ${g.titulo}` : ''}{(g.recebido || 0) > 0 ? ` · recebido ${fmt(g.recebido)}` : ''}</Text>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space wrap>
            <Popconfirm title="Mesclar? A linha do Notion some e a gêmea herda vínculo e realizado."
              onConfirm={() => onResolver(d, 'mesclar_duplicata', { irma_id: gemeaSel })}>
              <Button type="primary" disabled={!gemeaSel}>Mesclar na gêmea</Button>
            </Popconfirm>
            <Button onClick={() => onResolver(d, 'manter_duas', { nota })}>São duas parcelas mesmo — manter</Button>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehValor ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="warning" showIcon message="O valor difere entre o sistema e o Notion"
            description="Divergência registrada quando o Notion ainda era comparado (histórico — o sync foi desligado). Se o valor do Notion era o certo, adote-o; se o do sistema está certo, marque resolvida." />
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space wrap>
            <Popconfirm title={`Trocar o valor previsto de ${fmt(s.valor_sistema)} para ${fmt(s.valor_notion)}?`}
              onConfirm={() => onResolver(d, 'adotar_valor_notion', { nota })}>
              <Button type="primary">Adotar valor do Notion ({fmt(s.valor_notion)})</Button>
            </Popconfirm>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehSoSis ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="Esta parcela só existe no sistema — o Notion não a tem"
            description={`Pra comparar competência 1:1 com o Notion, crie a linha lá (contrato ${s.contrato_numero || d.contrato_numero || '?'}, valor idêntico de ${fmt(d.valor)}) — o robô vincula sozinho em até 5 min e este item sai da fila. Se o Notion não vai ter esta parcela (contrato novo que já nasceu no sistema), marque resolvida.`} />
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space wrap>
            <Button type="primary" onClick={() => onResolver(d, 'marcar_resolvida', { nota })}>
              O Notion não vai tê-la — marcar resolvida
            </Button>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehCaSolto ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="warning" showIcon message="Dinheiro do Conta Azul sem parcela do plano"
            description="Recebimentos que entraram no CA e não estão amarrados a nenhuma parcela — o realizado do contrato pode estar dobrado (recebido manual + espelho) ou órfão. Concilie cada linha na aba Financeiro do contrato; conforme conciliar, este item se atualiza e some sozinho. Se for o comportamento esperado (ex.: repasses parciais SEMA), marque resolvida." />
          {(s.maiores || []).length > 0 && (
            <div>
              <Text strong>Maiores linhas ({Math.min((s.maiores || []).length, 8)} de {s.qtd}):</Text>
              {(s.maiores || []).map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                  <Text style={{ width: 110, textAlign: 'right' }}>{fmt(l.valor)}</Text>
                  <Text type="secondary">{fmtVenc(l.data)} · {l.descricao}</Text>
                </div>
              ))}
            </div>
          )}
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space wrap>
            <Button type="primary" onClick={() => onResolver(d, 'marcar_resolvida', { nota })}>
              Marcar resolvida
            </Button>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehCredito ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="info" showIcon message="Negócio ganho sem crédito comercial"
            description={'Abra o negócio no Pipeline (aba Créditos) e registre quem fez proposta e fechamento — a pendência se obsoleta sozinha na próxima detecção. Use "Marcar resolvida" só se a conquista de fato não deve creditar ninguém (ex.: legado sem autor identificável).'} />
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space wrap>
            <Button type="primary" onClick={() => onResolver(d, 'marcar_resolvida', { nota })}>
              Não credita ninguém — marcar resolvida
            </Button>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehCompEmi ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="warning" showIcon
            message="A receita desta parcela cai na DRE num mês diferente do mês da NF"
            description={`Competência ${s.mes_competencia || '?'} × emissão ${s.mes_emissao || '?'}`
              + ` (${Math.abs(s.delta_meses || 0)} mês(es) de diferença). A política da casa é`
              + ` competência = faturamento. Se foi sem querer, "Usar mês da emissão" iguala.`
              + ` Se é intencional (receita pertence a outro período), "Manter divergência"`
              + ` registra o motivo — a decisão fica gravada e o caso não volta.`} />
          <Input.TextArea placeholder='Motivo (obrigatório pra "Manter divergência")' rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space wrap>
            <Popconfirm
              title={`Mudar a competência de ${fmtVenc(s.competencia)} para ${fmtVenc(s.emissao)}? A receita muda de mês na DRE.`}
              onConfirm={() => onResolver(d, 'usar_competencia_emissao', { nota })}>
              <Button type="primary">Usar mês da emissão ({s.mes_emissao || '?'})</Button>
            </Popconfirm>
            <Tooltip title={!nota.trim() ? 'Escreva o motivo da divergência primeiro' : ''}>
              <Button disabled={!nota.trim()}
                onClick={() => onResolver(d, 'marcar_resolvida', { nota })}>
                É intencional — manter divergência
              </Button>
            </Tooltip>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehOrfa ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          {candidatos.length > 0 ? (
            <>
              <Alert type="info" showIcon message="Unificar as três bases numa parcela só"
                description="Funde a cópia do Notion na parcela do PLANO (sistema) e amarra a linha do Conta Azul como o recebido." />
              <div>
                <Text strong>Plano (sistema) — a parcela que fica:</Text>
                <Radio.Group value={sistemaSel} onChange={e => setSistemaSel(e.target.value)}
                  style={{ display: 'block', marginTop: 4 }}>
                  <Space direction="vertical">
                    {candsSistema.map((c, i) => (
                      <Radio key={c.id} value={c.id}>
                        <Text strong>venc {fmtVenc(c.data_vencimento)}</Text>
                        <Text type="secondary"> · {i + 1}ª por vencimento{c.titulo ? ` · ${c.titulo}` : ''}</Text>
                      </Radio>
                    ))}
                    {candsSistema.length === 0 && <Text type="secondary">nenhuma parcela de plano encontrada</Text>}
                  </Space>
                </Radio.Group>
              </div>
              {candsCA.length > 0 && (
                <div>
                  <Text strong>Recebido (Conta Azul):</Text>
                  <Radio.Group value={caSel} onChange={e => setCaSel(e.target.value)}
                    style={{ display: 'block', marginTop: 4 }}>
                    <Space direction="vertical">
                      <Radio value="">não amarrar agora</Radio>
                      {candsCA.map((c, i) => (
                        <Radio key={c.id} value={c.id}>
                          <Text strong>venc {fmtVenc(c.data_vencimento)}</Text>
                          <Text type="secondary"> · {i + 1}ª por vencimento{c.titulo ? ` · ${c.titulo}` : ''}</Text>
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </div>
              )}
              <Button type="primary" disabled={!sistemaSel}
                onClick={() => onResolver(d, 'unificar_tres', { sistema_id: sistemaSel, ca_id: caSel || null })}>
                Unificar as três
              </Button>
            </>
          ) : (
            <Alert type="warning" showIcon
              message="Sem candidatos de plano — adote como parcela nova ou resolva manual." />
          )}
          <Space wrap style={{ marginTop: 8 }}>
            {d.contrato_id && (
              <Popconfirm title="Adotar a órfã como parcela nova do contrato?"
                onConfirm={() => onResolver(d, 'adotar_orfa', {})}>
                <Button>Adotar como parcela nova</Button>
              </Popconfirm>)}
            <Popconfirm title="Excluir a órfã? (cópia redundante do Notion)"
              onConfirm={() => onResolver(d, 'excluir_orfa', {})}>
              <Button danger>Excluir órfã</Button>
            </Popconfirm>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      ) : ehAvisoDoResultado(d.tipo) ? (
        // Aviso do resultado por contrato: quem resolve é a correção da causa
        // (o aviso some sozinho). Não há "Marcar resolvida" — dar baixa à mão
        // calaria para sempre um aviso que continua verdadeiro.
        <Space direction="vertical" style={{ width: '100%' }}>
          {comoResolver(d.tipo) && (
            <Alert type="info" showIcon message="Como resolver" description={comoResolver(d.tipo)} />
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>{SOBRE_IGNORAR}</Text>
          <Input.TextArea placeholder="Por que é de propósito (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space>
            {ACOES_DO_AVISO.map(a => (
              <Button key={a.v} onClick={() => onResolver(d, a.v, { nota })}>{a.l}</Button>
            ))}
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.TextArea placeholder="Nota da decisão (opcional)" rows={2}
            value={nota} onChange={e => setNota(e.target.value)} />
          <Space>
            <Button type="primary" onClick={() => onResolver(d, 'marcar_resolvida', { nota })}>Marcar resolvida</Button>
            <Button onClick={() => onResolver(d, 'ignorar', { nota })}>Ignorar</Button>
          </Space>
        </Space>
      )}
    </Modal>
  );
}
